import React, { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "../../components/ui/button";
import { toast } from "react-toastify";
import { LoaderPinwheel, Search, X, TrendingUp } from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer,
} from "recharts";
import IngresoForm from "../../components/ui/IngresoForm";
import { hasPermission } from "../../utils/permissions";
import PageSizeSelect from "../../components/ui/PageSizeSelect";

const AlmacenPage = () => {
  const [articulos, setArticulos] = useState([]);
  const [costosMes, setCostosMes] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  const [itemsPerPage, setItemsPerPage] = useState(10);
  const token = localStorage.getItem("token");
  const API_URL = process.env.REACT_APP_API_URL;

  const fetchArticulos = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/almacenes/consolidado`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setArticulos(res.data);
    } catch {
      toast.error("No se pudieron cargar los artículos del almacén");
    } finally {
      setLoading(false);
    }
  };

  const fetchCostos = async () => {
    try {
      const res = await axios.get(`${API_URL}/stock/costos-anuales`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCostosMes(res.data);
    } catch {
      toast.error("No se pudieron cargar los costos del año");
    }
  };

  useEffect(() => {
    fetchArticulos();
    fetchCostos();
    // eslint-disable-next-line
  }, []);

  useEffect(() => { setCurrentPage(1); }, [searchTerm, articulos]);

  const filteredArticulos = articulos.filter((item) => {
    const term = searchTerm.toLowerCase();
    return [item.articulo, item.codigo_item, item.stock_total, item.tipo_ultimo_movimiento]
      .some((v) => String(v ?? "").toLowerCase().includes(term));
  });

  const totalPages = Math.max(1, Math.ceil(filteredArticulos.length / itemsPerPage));
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedArticulos = filteredArticulos.slice(startIndex, startIndex + itemsPerPage);

  return (
    <div>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
        <div>
          <h2 className="text-lg font-semibold text-gray-800">Stock Consolidado</h2>
          <p className="text-sm text-gray-500">
            {articulos.length} artículo{articulos.length !== 1 ? "s" : ""} en todos los almacenes
          </p>
        </div>
        <Button onClick={() => setShowModal(true)} className="bg-blue-600 hover:bg-blue-700 text-white">
          + Nuevo ingreso
        </Button>
      </div>

      {loading && (
        <div className="mb-4 flex items-center gap-2 rounded-lg border border-blue-100 bg-blue-50 px-3 py-2 text-sm text-blue-700">
          <LoaderPinwheel className="h-4 w-4 animate-spin text-blue-600" />
          Cargando datos, por favor espera...
        </div>
      )}

      {/* Gráfica de costos */}
      {hasPermission("almacen.graficas") && costosMes.length > 0 && (
        <div className="mb-6 rounded-xl border border-gray-200 bg-gray-50 p-4">
          <div className="flex items-center gap-2 mb-3">
            <TrendingUp className="h-4 w-4 text-green-600" />
            <span className="text-sm font-medium text-gray-700">Costos anuales de ingresos</span>
          </div>
          <div className="h-52">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={costosMes}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="mes" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip formatter={(value) => `$${value.toLocaleString()}`} />
                <Bar dataKey="total" fill="#3b82f6" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Buscador */}
      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Buscar por artículo, código o tipo…"
          className="w-full sm:w-96 pl-9 pr-8 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-400 focus:outline-none"
        />
        {searchTerm && (
          <button onClick={() => setSearchTerm("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Tabla */}
      <div className="flex items-center justify-end mb-2">
        <PageSizeSelect
          value={itemsPerPage}
          onChange={(size) => {
            setItemsPerPage(size);
            setCurrentPage(1);
          }}
        />
      </div>
      <div className="overflow-x-auto rounded-xl border border-gray-200">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="bg-gray-100 text-gray-600 text-xs uppercase tracking-wide">
              <th className="px-4 py-3 text-left">#</th>
              <th className="px-4 py-3 text-left">Artículo</th>
              <th className="px-4 py-3 text-left">Código</th>
              <th className="px-4 py-3 text-center">Stock actual</th>
              <th className="px-4 py-3 text-left">Último movimiento</th>
              <th className="px-4 py-3 text-left">Tipo</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 bg-white">
            {paginatedArticulos.length > 0 ? (
              paginatedArticulos.map((item, idx) => (
                <tr key={item._id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3 text-gray-400 text-xs">{startIndex + idx + 1}</td>
                  <td className="px-4 py-3 font-medium text-gray-800">{item.articulo || "—"}</td>
                  <td className="px-4 py-3 text-gray-500 font-mono text-xs">{item.codigo_item || "—"}</td>
                  <td className="px-4 py-3 text-center">
                    <span className="inline-block px-2.5 py-0.5 rounded-full bg-blue-50 text-blue-700 font-semibold text-xs">
                      {item.stock_total}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-gray-500">
                    {item.fecha_ultimo_movimiento
                      ? new Date(item.fecha_ultimo_movimiento).toLocaleDateString()
                      : "—"}
                  </td>
                  <td className="px-4 py-3">
                    {item.tipo_ultimo_movimiento ? (
                      <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                        item.tipo_ultimo_movimiento === "Ingreso"
                          ? "bg-green-50 text-green-700"
                          : "bg-red-50 text-red-700"
                      }`}>
                        {item.tipo_ultimo_movimiento}
                      </span>
                    ) : "—"}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="6" className="py-12 text-center">
                  <Search className="h-8 w-8 text-gray-300 mx-auto mb-2" />
                  <p className="text-gray-500">No hay artículos para la búsqueda actual</p>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Paginación */}
      <div className="mt-4 flex items-center justify-between text-sm text-gray-600">
        <span className="text-xs text-gray-400">
          {filteredArticulos.length === 0 ? "0" : `${startIndex + 1}–${Math.min(startIndex + itemsPerPage, filteredArticulos.length)}`} de {filteredArticulos.length}
        </span>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setCurrentPage((p) => Math.max(1, p - 1))} disabled={currentPage === 1}>
            ← Anterior
          </Button>
          <span className="px-2 py-1 bg-gray-100 rounded-md text-xs">
            {currentPage} / {totalPages}
          </span>
          <Button variant="outline" size="sm" onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}>
            Siguiente →
          </Button>
        </div>
      </div>

      {showModal && (
        <IngresoForm
          onClose={() => setShowModal(false)}
          onSave={() => { setShowModal(false); fetchArticulos(); fetchCostos(); }}
        />
      )}
    </div>
  );
};

export default AlmacenPage;
